import {
  BarChart3Icon,
  CreditCardIcon,
  InstagramIcon,
  MailIcon,
  MusicIcon,
  TicketIcon,
  type LucideIcon,
} from 'lucide-react';

export type IntegrationGoalId = 'streaming' | 'social' | 'email' | 'shows' | 'money';

export interface IntegrationField {
  key: string;
  label: string;
  placeholder: string;
  helper?: string;
  secret?: boolean;
}

export interface IntegrationOption {
  id: string;
  name: string;
  /** Plain-language description of what this option is best for. */
  bestFor: string;
  /** What the artist must already have before starting. */
  youNeed: string;
  /** Simple, numbered instructions written for a non-technical user. */
  steps: string[];
  fields: IntegrationField[];
  helpUrl: string;
}

export interface IntegrationGoal {
  id: IntegrationGoalId;
  icon: LucideIcon;
  /** What the artist is trying to accomplish, in their words. */
  goal: string;
  /** What turns on inside ArtistOS once this is connected. */
  unlocks: string;
  /** Honest expectation about how fresh the data will be. */
  freshness: string;
  options: IntegrationOption[];
}

export const integrationGoals: IntegrationGoal[] = [
  {
    id: 'streaming',
    icon: MusicIcon,
    goal: 'Show my real streaming numbers',
    unlocks: 'Streaming Growth card and the streaming trend line in Mission Control',
    freshness: 'Updates once a day. Streaming platforms report 1–3 days behind.',
    options: [
      {
        id: 'spotify-artists',
        name: 'Spotify for Artists',
        bestFor: 'Monthly listeners, followers, and top cities',
        youNeed: 'Admin access to your Spotify for Artists profile',
        steps: [
          'Open Spotify for Artists and sign in as the account admin.',
          'Go to your artist profile page and copy the web address.',
          'Paste that address below and choose Connect.',
          'Approve ArtistOS when Spotify asks for permission.',
        ],
        fields: [
          { key: 'profileUrl', label: 'Spotify artist profile URL', placeholder: 'https://open.spotify.com/artist/...' },
        ],
        helpUrl: 'https://artists.spotify.com',
      },
      {
        id: 'apple-artists',
        name: 'Apple Music for Artists',
        bestFor: 'Apple plays, Shazams, and listener locations',
        youNeed: 'Access to your Apple Music for Artists account',
        steps: [
          'Sign in to Apple Music for Artists.',
          'Copy your artist ID from the address bar of your profile page.',
          'Paste it below and choose Connect.',
        ],
        fields: [
          { key: 'artistId', label: 'Apple artist ID', placeholder: '1234567890' },
        ],
        helpUrl: 'https://artists.apple.com',
      },
      {
        id: 'distributor',
        name: 'My distributor',
        bestFor: 'All platforms in one place, plus royalty figures',
        youNeed: 'A DistroKid, TuneCore, CD Baby, or similar account',
        steps: [
          'Sign in to your distributor and open the analytics or API section.',
          'Create an API key if one is offered.',
          'Paste the key below. If your distributor has no API, choose CSV upload instead.',
        ],
        fields: [
          { key: 'distributor', label: 'Distributor name', placeholder: 'DistroKid' },
          { key: 'apiKey', label: 'API key', placeholder: 'Paste your key', secret: true, helper: 'Stored securely. Never shown again after saving.' },
        ],
        helpUrl: 'https://distrokid.com',
      },
    ],
  },
  {
    id: 'social',
    icon: InstagramIcon,
    goal: 'Track my followers and engagement',
    unlocks: 'Followers and Social Growth cards in Mission Control',
    freshness: 'Updates once a day.',
    options: [
      {
        id: 'instagram',
        name: 'Instagram',
        bestFor: 'Follower count and engagement rate',
        youNeed: 'A Business or Creator account, not a personal one',
        steps: [
          'Open Instagram, go to Settings, then Account type and tools.',
          'Switch to a Business or Creator account if you have not already.',
          'Choose Connect below and sign in with that account.',
        ],
        fields: [
          { key: 'handle', label: 'Instagram handle', placeholder: '@coopertyband' },
        ],
        helpUrl: 'https://help.instagram.com',
      },
      {
        id: 'tiktok',
        name: 'TikTok',
        bestFor: 'Follower growth and video reach',
        youNeed: 'A TikTok account with analytics turned on',
        steps: [
          'Open TikTok and switch to a Business account under Settings.',
          'Choose Connect below and approve access.',
        ],
        fields: [
          { key: 'handle', label: 'TikTok handle', placeholder: '@coopertyband' },
        ],
        helpUrl: 'https://www.tiktok.com/business',
      },
      {
        id: 'youtube',
        name: 'YouTube',
        bestFor: 'Subscribers and video views',
        youNeed: 'The Google account that owns your channel',
        steps: [
          'Copy your YouTube channel URL.',
          'Paste it below and choose Connect.',
          'Sign in with the Google account that owns the channel.',
        ],
        fields: [
          { key: 'channelUrl', label: 'Channel URL', placeholder: 'https://youtube.com/@...' },
        ],
        helpUrl: 'https://support.google.com/youtube',
      },
    ],
  },
  {
    id: 'email',
    icon: MailIcon,
    goal: 'Actually send my fan emails and texts',
    unlocks: 'Real delivery for Fans & VIP campaigns and the Email Subscribers card',
    freshness: 'Subscriber counts update after each send and once a day.',
    options: [
      {
        id: 'mailerlite',
        name: 'MailerLite',
        bestFor: 'Recommended — free for 1,000 fans, keeps your own form design',
        youNeed: 'A free MailerLite account with a group for your VIP list',
        steps: [
          'Create a free account at mailerlite.com.',
          'Create a group called VIP — this becomes your fan list.',
          'Turn on a welcome automation so new fans get an email the moment they join.',
          'Open Integrations, then API, and generate a key. Copy it.',
          'Paste the key and your group name below.',
          'Verify coopertyband.com as your sending domain so email does not land in spam.',
        ],
        fields: [
          { key: 'apiKey', label: 'API key', placeholder: 'Paste your key', secret: true, helper: 'Found under Integrations → API in MailerLite.' },
          { key: 'group', label: 'Group name', placeholder: 'VIP' },
        ],
        helpUrl: 'https://www.mailerlite.com',
      },
      {
        id: 'mailchimp',
        name: 'Mailchimp',
        bestFor: 'Simple newsletters with templates',
        youNeed: 'A Mailchimp account and an audience list',
        steps: [
          'Sign in to Mailchimp and open Account, then Extras, then API keys.',
          'Create a key and copy it.',
          'Paste the key and your audience name below.',
        ],
        fields: [
          { key: 'apiKey', label: 'API key', placeholder: 'Paste your key', secret: true },
          { key: 'audience', label: 'Audience name', placeholder: 'Cooper Ty Band fans' },
        ],
        helpUrl: 'https://mailchimp.com/help',
      },
      {
        id: 'klaviyo',
        name: 'Klaviyo',
        bestFor: 'Email plus SMS in one place',
        youNeed: 'A Klaviyo account with SMS enabled if you want texts',
        steps: [
          'Sign in to Klaviyo and open Settings, then API keys.',
          'Create a private key and copy it.',
          'Paste it below and choose Connect.',
        ],
        fields: [
          { key: 'apiKey', label: 'Private API key', placeholder: 'Paste your key', secret: true },
        ],
        helpUrl: 'https://help.klaviyo.com',
      },
      {
        id: 'twilio',
        name: 'Twilio',
        bestFor: 'Text messages only',
        youNeed: 'A Twilio account and an approved sending number',
        steps: [
          'Sign in to Twilio and open the Console dashboard.',
          'Copy your Account SID and Auth Token.',
          'Paste them below along with your sending number.',
        ],
        fields: [
          { key: 'accountSid', label: 'Account SID', placeholder: 'AC...' },
          { key: 'authToken', label: 'Auth token', placeholder: 'Paste your token', secret: true },
          { key: 'fromNumber', label: 'Sending number', placeholder: '+1 512 555 0114' },
        ],
        helpUrl: 'https://www.twilio.com/docs',
      },
    ],
  },
  {
    id: 'shows',
    icon: TicketIcon,
    goal: 'Keep my tour dates in sync',
    unlocks: 'Upcoming Shows in Mission Control and tour dates on your website',
    freshness: 'Updates within about an hour of a change.',
    options: [
      {
        id: 'bandsintown',
        name: 'Bandsintown',
        bestFor: 'Automatically pulling in your announced shows',
        youNeed: 'A claimed Bandsintown for Artists profile',
        steps: [
          'Sign in to Bandsintown for Artists.',
          'Open Settings and find your artist ID or profile URL.',
          'Paste it below and choose Connect.',
        ],
        fields: [
          { key: 'profileUrl', label: 'Bandsintown profile URL', placeholder: 'https://bandsintown.com/a/...' },
        ],
        helpUrl: 'https://artists.bandsintown.com',
      },
      {
        id: 'google-calendar',
        name: 'Google Calendar',
        bestFor: 'Putting confirmed shows on your personal calendar',
        youNeed: 'The Google account you use for scheduling',
        steps: [
          'Choose Connect below.',
          'Sign in with your Google account and allow calendar access.',
          'Pick which calendar shows should be added to.',
        ],
        fields: [
          { key: 'calendarName', label: 'Calendar name', placeholder: 'Tour dates' },
        ],
        helpUrl: 'https://support.google.com/calendar',
      },
    ],
  },
  {
    id: 'money',
    icon: CreditCardIcon,
    goal: 'See what I actually earned',
    unlocks: 'Revenue card in Mission Control and merch checkout tracking',
    freshness: 'Merch sales update hourly. Royalties arrive monthly from your distributor.',
    options: [
      {
        id: 'stripe',
        name: 'Stripe',
        bestFor: 'Merch and direct payment links',
        youNeed: 'A Stripe account that has completed verification',
        steps: [
          'Sign in to Stripe and open Developers, then API keys.',
          'Copy your secret key.',
          'Paste it below and choose Connect.',
        ],
        fields: [
          { key: 'secretKey', label: 'Secret key', placeholder: 'sk_live_...', secret: true },
        ],
        helpUrl: 'https://stripe.com/docs/keys',
      },
      {
        id: 'shopify',
        name: 'Shopify',
        bestFor: 'A full merch store with inventory',
        youNeed: 'A Shopify store and admin access',
        steps: [
          'Sign in to Shopify admin and open Apps, then Develop apps.',
          'Create an app and copy the Admin API access token.',
          'Paste it below with your store address.',
        ],
        fields: [
          { key: 'storeDomain', label: 'Store address', placeholder: 'coopertyband.myshopify.com' },
          { key: 'accessToken', label: 'Admin API token', placeholder: 'Paste your token', secret: true },
        ],
        helpUrl: 'https://help.shopify.com',
      },
    ],
  },
];

export interface IntegrationConnection {
  optionId: string;
  goalId: IntegrationGoalId;
  label: string;
  connectedAt: string;
}

export const integrationsStorageKey = 'artistos-integrations';
export const integrationsUpdatedEvent = 'artistos:integrations-updated';

export function getIntegrationConnections(): IntegrationConnection[] {
  try {
    const stored = window.localStorage.getItem(integrationsStorageKey);
    if (!stored) return [];
    const parsed: unknown = JSON.parse(stored);
    return Array.isArray(parsed) ? (parsed as IntegrationConnection[]) : [];
  } catch {
    return [];
  }
}

export function saveIntegrationConnection(connection: IntegrationConnection): boolean {
  try {
    const next = [...getIntegrationConnections().filter((item) => item.optionId !== connection.optionId), connection];
    persist(next);
    return true;
  } catch {
    return false;
  }
}

export function removeIntegrationConnection(optionId: string): boolean {
  try {
    persist(getIntegrationConnections().filter((item) => item.optionId !== optionId));
    return true;
  } catch {
    return false;
  }
}

export const integrationStatusIcon = BarChart3Icon;

function persist(connections: IntegrationConnection[]) {
  window.localStorage.setItem(integrationsStorageKey, JSON.stringify(connections));
  window.dispatchEvent(new CustomEvent(integrationsUpdatedEvent, { detail: connections }));
}
